import { useState } from "react";

import { makePostApiCall, makeGetApiCall } from "../../shared/makeApiCall";
import DeleteConfirmationForm from "../../components/forms/DeleteConfirmationForm";
import { generateUniqueNumber } from "../../shared/utility";
import ModelErrorComponent from "../../components/modals/ModelErrorComponent";
import { SnackbarData, SnackbarType } from "../../components/Snackbar";
import ProgressModal from "../../components/modals/ProgressModal";
import {
  cleanFiltersForRequest,
  serializeMultiSelectFiltersForRequest,
} from "../../shared/filterUtils";

export function ManageOrdersViewModel(logout, addSnackbar) {
  const [customers, setCustomers] = useState([]);

  const [filters, setFilters] = useState({
    customer: [],
    orderStatus: [],
    startDate: null,
    endDate: null,
  });
  const [emptyModal, setEmptyModelOpen] = useState({
    open: false,
    childComponent: null,
  });
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState({ data: [] });
  const [showFilters, setShowFilters] = useState(false);
  const [slideOver, setSlideOver] = useState({
    childComponent: null,
    open: false,
  });
  const [analytics, setAnalytics] = useState({
    range: null,
    stats: [],
    loading: false,
  });
  const [showDates, setShowDates] = useState(false);

  const showProgressModal = (message = "Processing...") => {
    setEmptyModelOpen({
      childComponent: <ProgressModal message={message} />,
      open: true,
    });
  };

  const hideProgressModal = () => {
    setEmptyModelOpen({
      childComponent: null,
      open: false,
    });
  };

  const getOrders = async function (search, page, filterArg) {
    setLoading(true);
    const payload = {
      page: page,
      filters: serializeMultiSelectFiltersForRequest(
        cleanFiltersForRequest({ ...filterArg, search: search }),
      ),
    };
    let callResult = await makePostApiCall("fetchOrders", payload);

    if (callResult.success) {
      setOrders(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
    setLoading(false);
  };

  const showErrorModal = (title, subtitle) => {
    setEmptyModelOpen({
      childComponent: (
        <ModelErrorComponent
          setOpen={setEmptyModelOpen}
          subtitle={subtitle}
          title={title}
        />
      ),
      open: true,
    });
  };

  const deleteOrder = async function (id) {
    let callResult = await makePostApiCall(`deleteOrder/${id}`);

    if (callResult.success) {
      const filteredItems = orders.data.filter((order) => order.id !== id);
      setOrders({ ...orders, data: filteredItems });

      setEmptyModelOpen({
        open: false,
        childComponent: null,
      });
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          callResult.data.message,
          SnackbarType.Success,
        ),
      );
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
  };

  const askDeleteOrder = function (order) {
    setEmptyModelOpen({
      childComponent: (
        <DeleteConfirmationForm
          buttonMessage="Delete"
          confirmAction={() => deleteOrder(order.id)}
          message={`Are you sure you want to delete ${order.po_number}?`}
          setOpen={setEmptyModelOpen}
        />
      ),
      open: true,
    });
  };

  const getCustomersForDropdown = async function () {
    let callResult = await makePostApiCall(`getCustomersForDropdown`);
    if (callResult.success) {
      setCustomers(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const resetFilters = () => {
    const clearedFilters = {
      customer: [],
      orderStatus: [],
      startDate: null,
      endDate: null,
    };
    setFilters(clearedFilters);

    setShowFilters(false);
    getOrders(null, 1, clearedFilters);
  };

  const fetchEstimates = async function () {
    showProgressModal("Fetching estimates...");
    let callResult = await makeGetApiCall(`quickbooks/fetchEstimates`);
    hideProgressModal();
    if (callResult.success) {
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          callResult.data.message,
          SnackbarType.Success,
        ),
      );
      getOrders(null, 1, filters);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
  };

  return {
    customers,
    getCustomersForDropdown,
    getOrders,
    loading,
    orders,
    setEmptyModelOpen,
    setSlideOver,
    slideOver,
    emptyModal,
    askDeleteOrder,
    filters,
    resetFilters,
    setFilters,
    setShowFilters,
    showFilters,
    fetchEstimates,
    analytics,
    showDates,
    setShowDates,
  };
}
